import React, { useState, useEffect } from 'react';
import { 
  updateSyncConfig, 
  manualSync, 
  batchSyncAll, 
  getSyncStatus, 
  getSyncHistory 
} from '../api/auth';
import { useNotification } from '../contexts/NotificationContext';

const TranscriptSyncModal = ({ project, onClose, onUpdate }) => {
  const { showSuccess, showError, showWarning } = useNotification();
  
  const [config, setConfig] = useState({
    auto_sync_enabled: false,
    sync_frequency: 'manual',
    sync_time: '02:00',
    sync_day_of_week: 0
  });
  
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [batchSyncing, setBatchSyncing] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  useEffect(() => {
    if (project) {
      loadSyncData();
    }
  }, [project]);
  
  const loadSyncData = async () => {
    try {
      setLoading(true);
      const [statusData, historyData] = await Promise.all([
        getSyncStatus(project.id),
        getSyncHistory(project.id, 10)
      ]);
      
      setSyncStatus(statusData);
      setSyncHistory(historyData.syncLogs || []);
      
      // Set config from project data
      setConfig({
        auto_sync_enabled: statusData.project.auto_sync_enabled || false,
        sync_frequency: statusData.project.sync_frequency || 'manual',
        sync_time: statusData.project.sync_time || '02:00',
        sync_day_of_week: statusData.project.sync_day_of_week || 0
      });
    } catch (error) {
      console.error('Error loading sync data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveConfig = async () => {
    try {
      setLoading(true);
      await updateSyncConfig(project.id, config);
      showSuccess('Success', 'Sync configuration updated');
      await loadSyncData();
      if (onUpdate) onUpdate();
    } catch (error) {
      showError('Error', error.message || 'Failed to update configuration');
    } finally {
      setLoading(false);
    }
  };
  
  const handleManualSync = async () => {
    try {
      setSyncing(true);
      showWarning('Syncing', 'Manual sync started. This may take a few minutes...');
      const result = await manualSync(project.id);
      showSuccess('Success', `Synced ${result.stats.new + result.stats.updated} transcripts`);
      await loadSyncData();
      if (onUpdate) onUpdate();
    } catch (error) {
      showError('Error', error.message || 'Failed to sync transcripts');
    } finally {
      setSyncing(false);
    }
  };
  
  const handleBatchSyncAll = async () => {
    try {
      setBatchSyncing(true);
      showWarning('Batch Syncing', 'Syncing ALL transcripts. This will take several minutes...');
      const result = await batchSyncAll(project.id);
      showSuccess('Success', `Batch sync complete! New: ${result.stats.new}, Updated: ${result.stats.updated}`);
      await loadSyncData();
      if (onUpdate) onUpdate();
    } catch (error) {
      showError('Error', error.message || 'Failed to batch sync');
    } finally {
      setBatchSyncing(false);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  const getDayName = (day) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day] || 'Sunday';
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-600 dark:text-green-400';
      case 'failed': return 'text-red-600 dark:text-red-400';
      case 'in_progress': return 'text-blue-600 dark:text-blue-400';
      case 'never': return 'text-gray-500 dark:text-gray-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-semibold">Success</span>;
      case 'failed':
        return <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-xs font-semibold">Failed</span>;
      case 'in_progress':
        return <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-semibold">In Progress</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-semibold">Completed</span>;
      case 'started':
        return <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full text-xs font-semibold">Started</span>;
      case 'never':
        return <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs font-semibold">Never Run</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs font-semibold">{status}</span>;
    }
  };
  
  if (!project) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border dark:border-gray-800 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b dark:border-gray-800">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Transcript Auto-Sync
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {project.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : (
            <>
              {/* Current Status Card */}
              <div className="bg-gradient-to-r from-[#32B1FF]/10 to-[#2A9FEB]/10 dark:from-[#32B1FF]/20 dark:to-[#2A9FEB]/20 rounded-xl p-5 border border-[#32B1FF]/30">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Current Status
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Last Sync</div>
                    <div className={`text-sm font-semibold ${getStatusColor(syncStatus?.project?.last_sync_status)}`}>
                      {formatDate(syncStatus?.project?.last_sync_at)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Status</div>
                    <div>{getStatusBadge(syncStatus?.project?.last_sync_status)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Last Synced</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {syncStatus?.project?.last_sync_count || 0} transcripts
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Stored</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {syncStatus?.total_transcripts || 0} transcripts
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Configuration Section */}
              <div className="border dark:border-gray-800 rounded-xl p-5">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Auto-Sync Configuration
                </h3>
                
                {/* Enable Toggle */}
                <div className="mb-5 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">Enable Auto-Sync</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Automatically sync transcripts from Voiceflow
                      </div>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={config.auto_sync_enabled}
                        onChange={(e) => setConfig({ ...config, auto_sync_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-[#32B1FF] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                    </div>
                  </label>
                </div>
                
                {/* Frequency Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sync Frequency
                  </label>
                  <select
                    value={config.sync_frequency}
                    onChange={(e) => setConfig({ ...config, sync_frequency: e.target.value })}
                    disabled={!config.auto_sync_enabled}
                    className="w-full border dark:border-gray-700 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="manual">Manual Only</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
                
                {/* Time Selection (for daily/weekly) */}
                {config.sync_frequency !== 'manual' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sync Time (24h format)
                    </label>
                    <input
                      type="time"
                      value={config.sync_time}
                      onChange={(e) => setConfig({ ...config, sync_time: e.target.value })}
                      disabled={!config.auto_sync_enabled}
                      className="w-full border dark:border-gray-700 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Server timezone: America/Chicago (MST/CST)
                    </p>
                  </div>
                )}
                
                {/* Day of Week Selection (for weekly) */}
                {config.sync_frequency === 'weekly' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Day of Week
                    </label>
                    <select
                      value={config.sync_day_of_week}
                      onChange={(e) => setConfig({ ...config, sync_day_of_week: parseInt(e.target.value) })}
                      disabled={!config.auto_sync_enabled}
                      className="w-full border dark:border-gray-700 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="0">Sunday</option>
                      <option value="1">Monday</option>
                      <option value="2">Tuesday</option>
                      <option value="3">Wednesday</option>
                      <option value="4">Thursday</option>
                      <option value="5">Friday</option>
                      <option value="6">Saturday</option>
                    </select>
                  </div>
                )}
                
                {/* Schedule Summary */}
                {config.auto_sync_enabled && config.sync_frequency !== 'manual' && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                    <div className="text-sm text-blue-900 dark:text-blue-300">
                      <strong>Schedule:</strong>{' '}
                      {config.sync_frequency === 'daily' 
                        ? `Every day at ${config.sync_time}`
                        : `Every ${getDayName(config.sync_day_of_week)} at ${config.sync_time}`
                      }
                    </div>
                  </div>
                )}
                
                {/* Save Button */}
                <button
                  onClick={handleSaveConfig}
                  disabled={loading}
                  className="w-full px-4 py-2.5 bg-[#32B1FF] hover:bg-[#2A9FEB] text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
              
              {/* Manual Actions */}
              <div className="border dark:border-gray-800 rounded-xl p-5">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Manual Actions
                </h3>
                
                <div className="space-y-3">
                  {/* Manual Sync Button */}
                  <button
                    onClick={handleManualSync}
                    disabled={syncing || batchSyncing}
                    className="w-full px-4 py-3 border-2 border-[#32B1FF] text-[#32B1FF] hover:bg-[#32B1FF] hover:text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {syncing ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Syncing...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Sync Latest Transcripts
                      </>
                    )}
                  </button>
                  
                  {/* Batch Sync All Button */}
                  <button
                    onClick={handleBatchSyncAll}
                    disabled={syncing || batchSyncing}
                    className="w-full px-4 py-3 border-2 border-purple-500 text-purple-600 dark:text-purple-400 hover:bg-purple-500 hover:text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {batchSyncing ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Batch Syncing...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Batch Sync ALL Transcripts
                      </>
                    )}
                  </button>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    <strong>Note:</strong> Manual sync gets latest 50 transcripts. Batch sync gets ALL transcripts (may take several minutes).
                  </p>
                </div>
              </div>
              
              {/* Sync History */}
              <div className="border dark:border-gray-800 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Sync History
                  </h3>
                  <button
                    onClick={loadSyncData}
                    disabled={historyLoading}
                    className="text-sm text-[#32B1FF] hover:text-[#2A9FEB] font-medium"
                  >
                    Refresh
                  </button>
                </div>
                
                {syncHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No sync history yet
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {syncHistory.map((log) => (
                      <div
                        key={log.id}
                        className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {getStatusBadge(log.status)}
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {log.sync_type.replace('_', ' ').toUpperCase()}
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {formatDate(log.started_at)}
                            </div>
                            {log.status === 'completed' && (
                              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                New: {log.new_count} • Updated: {log.updated_count} • Failed: {log.failed_count} • Duration: {log.duration_seconds}s
                              </div>
                            )}
                            {log.error_message && (
                              <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                                {log.error_message}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranscriptSyncModal;
